import { Button, Paper } from "@mui/material";
import { useProductFeatures } from "../../hooks/useProductFeatures";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";

export const FeatureFlags = () => {
  // eslint-disable-next-line no-unused-vars
  const [debugCounter, setDebugCounter] = useState(0);
  const posthog = usePostHog();

  const posthogFlags = useMemo(
    () => posthog.featureFlags.getFlagsWithDetails(),
    [posthog],
  );

  const { productPlans, setDebugProductPlan, isDebugOverride } =
    useProductFeatures();
  const [isCoreFitEnabled, setIsCoreFitEnabled] = useFeatureFlag(
    "CORE_FIT",
    false,
  );
  const [isKnowledgeCheckEnabled, setIsKnowledgeCheckEnabled] = useFeatureFlag(
    "KNOWLEDGE_CHECK",
    false,
  );
  const [isFavoriteHatEnabled, setIsFavoriteHatEnabled] = useFeatureFlag(
    "FAVORITE_HAT",
    false,
  );

  const [isDraftsEnabled, setIsDraftsEnabled] = useFeatureFlag(
    "CMS_DRAFTS",
    false,
  );
  const [isCacheEnabled, setIsCacheEnabled] = useFeatureFlag("CMS_CACHE", true);

  let availableFeatures = [
    "6.3-possibilities-content",
    "clarity-content",
    "dev-only",
    "launch-content",
    "ms-possibilities-content",
    "ms-possibilities-ux",
    "possibilities-content",
    "proskills-child-youth-development",
    "proskills-customer-engagement-fundamentals",
    "proskills-data-science-analytics",
    "proskills-dev",
    "proskills-ux-design-fundamentals",
    "proskills-web-development-fundamentals",
    "proskills-agile-explorer",
    "proskills-explore-emerging-tech",
    "syep-content",
    "syep-oy-cr",
    "syep-oy-cy",
    "syep-yy-cr",
    "syep-yy-cy",
    "wbl-capstone",
    "wbl-career-prep",
    "wbl-practicum",
    "wbl-tracking",
    "workforce-generic",
  ];

  // Add any product plans that aren't in availableFeatures
  availableFeatures = [...new Set([...availableFeatures, ...productPlans])];

  const resetAll = () => {
    window.localStorage.removeItem("feature_flags");
    window.localStorage.removeItem("debug_product_plan");
    window.location.reload();
  };

  const toggleFeature = (feature) => {
    const updatedPlans = productPlans.includes(feature)
      ? productPlans.filter((plan) => plan !== feature)
      : [...productPlans, feature];
    setDebugProductPlan(updatedPlans);
    setDebugCounter((c) => c + 1);
  };

  return (
    <Paper sx={{ px: 5 }}>
      <h1>Posthog Flags</h1>
      <ul>
        {Object.values(posthogFlags).map((flag) => (
          <>
            <li key={flag.key}>
              {flag.key} {flag.enabled ? "✅ Enabled" : "❌ Disabled"}
            </li>
          </>
        ))}
      </ul>
      <h1>Summary</h1>
      <p>Here is what content you will see in the app with these flags:</p>
      <ul>
        <li>
          {isDraftsEnabled && (
            <p>
              ✅ You will see drafts of published content. You will not see
              content that was never published.
            </p>
          )}
          {isDraftsEnabled || (
            <p>
              ❌ You will not see drafts, only published versions of content.
            </p>
          )}
        </li>
        <li>
          {isCacheEnabled && (
            <p>
              ⌚Changes to content in the CMS will be cached and take a few
              minutes to appear.
            </p>
          )}
          {isCacheEnabled || (
            <p>
              ⚡Changes to content in the CMS will not be delayed. Content
              loading might take longer than normal.
            </p>
          )}
        </li>

        <li>
          {productPlans.includes("dev-only") && (
            <p>
              🧑‍💻You will see content from the CMS if it is in the{" "}
              <b>dev-only</b> plan.
            </p>
          )}
          {productPlans.includes("dev-only") || (
            <p>
              🏢You will only see content from the CMS if it is in the workforce
              plan.
            </p>
          )}
        </li>

        <li>
          {isKnowledgeCheckEnabled && (
            <p>✅You will see the knowledge check button on the hat page.</p>
          )}
          {isKnowledgeCheckEnabled || (
            <p>
              ❌You will not see the knowledge check button on the hat page.
            </p>
          )}
        </li>
        <li>
          {isCoreFitEnabled && (
            <p>✅You will see the check my fit button on the hat page.</p>
          )}
          {isCoreFitEnabled || (
            <p>❌You will not see the check my fit button on the hat page.</p>
          )}
        </li>
        <li>
          {isFavoriteHatEnabled && (
            <p>✅You will see the favorite hat button on the hat page.</p>
          )}
          {isFavoriteHatEnabled || (
            <p>❌You will not see the favorite hat button on the hat page.</p>
          )}
        </li>
      </ul>
      <h1>Feature Flags</h1>
      <ul>
        <li>
          <h2>CMS Flags:</h2>
          <ul>
            <li>
              <Button
                variant="outlined"
                onClick={() => setIsDraftsEnabled(!isDraftsEnabled)}
              >
                {isDraftsEnabled ? "Disable Drafts" : "Enable Drafts"}
              </Button>
              <p>
                Should we load draft content from the CMS? This is so we can see
                draft versions of previously published content.
              </p>
            </li>
            <li>
              <Button
                variant="outlined"
                onClick={() => setIsCacheEnabled(!isCacheEnabled)}
              >
                {isCacheEnabled ? "Disable Cache" : "Enable Cache"}
              </Button>
              <p>
                Should we cache the CMS data? When this is enabled, it operates
                like production, when it&apos;s disabled, there is no delay in
                loading updates from the CMS
              </p>
            </li>
          </ul>
        </li>
        <li>
          <h2>Hats Flags:</h2>
          <ul>
            <li>
              <Button
                variant="outlined"
                onClick={() =>
                  setIsKnowledgeCheckEnabled(!isKnowledgeCheckEnabled)
                }
              >
                {isKnowledgeCheckEnabled
                  ? "Disable Knowledge Check"
                  : "Enable Knowledge Check"}
              </Button>
              <p>Should we show the knowledge check button on the hat page?</p>
            </li>
            <li>
              <Button
                variant="outlined"
                onClick={() => setIsCoreFitEnabled(!isCoreFitEnabled)}
              >
                {isCoreFitEnabled
                  ? "Disable Core Fit Check Button"
                  : "Enable Core Fit Check Button"}
              </Button>
              <p>Should we show the check my fit on the hat page?</p>
            </li>
            <li>
              <Button
                variant="outlined"
                onClick={() => setIsFavoriteHatEnabled(!isFavoriteHatEnabled)}
              >
                {isFavoriteHatEnabled
                  ? "Disable Favorite Hat"
                  : "Enable Favorite Hat"}
              </Button>
            </li>
          </ul>
        </li>
      </ul>

      <h1>Product Plans / Features</h1>
      <p>Current active Product Features: {productPlans.join(", ")}</p>
      <p>
        Is Debug Override:{" "}
        {isDebugOverride
          ? "Yes - using fake product plans"
          : "No - using the climbers real product plan"}
      </p>

      <table>
        <tbody>
          {availableFeatures.map((feature) => (
            <tr key={feature}>
              <td>
                <Button
                  variant="outlined"
                  onClick={() => toggleFeature(feature)}
                >
                  {productPlans.includes(feature)
                    ? `Disable ${feature}`
                    : `Enable ${feature}`}
                </Button>
              </td>
              <td>
                Status: {productPlans.includes(feature) ? "✅ On" : "❌ Off"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="outlined" onClick={() => setDebugProductPlan(null)}>
        Reset to default
      </Button>

      <h1>Reset All</h1>
      <Button color="error" variant="outlined" onClick={resetAll}>
        Reset All
      </Button>
    </Paper>
  );
};
